import React, { useState, useEffect } from "react";
import {
  Route,
  Switch,
  Redirect,
  useRouteMatch,
  useHistory,
  useLocation,
} from "react-router-dom";
import styles from "./CategoryBuilder.module.css";
import { useCategoryBuilder } from "./useCategoryBuilder";

import Card from "shared/Card";
import Select from "../shared/Select";
import { ReactComponent as ArchiveButton } from "./icons/archive_app_btn.svg";
import { ReactComponent as UnarchiveButton } from "./icons/unarchive_app_btn.svg";
import { ReactComponent as NewButton } from "./icons/new_app_btn.svg";
import AppCategories from "./AppCategories";
import SSF from "./SSF";
import AddNewAppModal from "./components/AddNewAppModal";
import ArchiveAppModal from "./components/ArchiveAppModal";
import UnarchiveAppModal from "./components/UnarchiveAppModal";

export function CategoryBuilder() {
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const {
    categories,
    categoriesIsLoading,
    newApplicationPost,
    archiveApplicationPut,
  } = useCategoryBuilder();

  const [showNewAppModal, setShowNewAppModal] = useState(false);
  const [showArchiveAppModal, setShowArchiveAppModal] = useState(false);
  const [showUnarchiveAppModal, setShowUnarchiveAppModal] = useState(false);
  const [newAppName, setNewAppName] = useState("");
  const archivedApplications = (categories || []).filter(
    (category) => !category.isActive
  );

  const [optionList, setOptionList] = useState([]);
  const [currentOption, setCurrentOption] = useState({
    title: "",
    pathname: "",
    appId: null,
  });

  // populate option list and push to first app in list
  useEffect(() => {
    if (categories && categories.length > 0) {
      var activeCategories = categories.filter((category) => category.isActive);
      setOptionList(
        activeCategories.map((category) => {
          return {
            title: category.name,
            pathname: `${path}/${category.name
              .replace(/\s/g, "")
              .toLowerCase()}`,
            appId: category.applicationTypeId,
          };
        })
      );
    }
  }, [categories, path]);

  // set current option
  useEffect(() => {
    !categoriesIsLoading &&
      optionList &&
      optionList.length > 0 &&
      setCurrentOption(
        optionList.find((option) => option.pathname === location.pathname)
          ? optionList.find((option) => option.pathname === location.pathname)
          : {
              title: "",
              pathname: "",
              appId: null,
            }
      );
  }, [optionList, categoriesIsLoading, location.pathname]);

  // new app modal
  const handleNewAppModalClose = () => {
    setShowNewAppModal(false);
    setNewAppName("");
  };

  // submit new app
  const handleNewAppSubmit = async (e) => {
    await newApplicationPost.mutateAsync({ name: e });
  };

  // archive app
  const handleArchiveAppSubmit = async (e) => {
    const result = await archiveApplicationPut.mutateAsync({
      applicationTypeId: e,
      isActive: "false",
    });
    if (!result.success) return;

    history.push(
      optionList && optionList.length > 0 && optionList[0].pathname
        ? optionList[0].pathname
        : "/"
    );
  };

  const handleUnarchiveAppSubmit = (applicationTypeId) =>
    archiveApplicationPut.mutateAsync({
      applicationTypeId,
      isActive: "true",
    });

  return (
    <Card>
      {showNewAppModal && (
        <AddNewAppModal
          onClose={handleNewAppModalClose}
          onChange={(e) => setNewAppName(e)}
          value={newAppName}
          onSubmit={handleNewAppSubmit}
        />
      )}

      {showArchiveAppModal && (
        <ArchiveAppModal
          onClose={() => setShowArchiveAppModal(false)}
          onSubmit={handleArchiveAppSubmit}
          appNumber={currentOption.appId}
        />
      )}
      {showUnarchiveAppModal && (
        <UnarchiveAppModal
          apps={archivedApplications}
          onClose={() => setShowUnarchiveAppModal(false)}
          onSubmit={handleUnarchiveAppSubmit}
        />
      )}

      <header className={styles.header}>
        <h3>Category Builder</h3>

        <div className={styles.select}>
          <Select
            options={optionList.map((option) => option.title)}
            selectedOption={
              !categoriesIsLoading ? currentOption.title : "Loading"
            }
            onChange={(e) =>
              history.push(
                optionList.find(
                  (option) => e.toLowerCase() === option.title.toLowerCase()
                )
                  ? optionList.find(
                      (option) => e.toLowerCase() === option.title.toLowerCase()
                    ).pathname
                  : ""
              )
            }
            disabled={optionList.length < 1 || categoriesIsLoading}
          />
        </div>
        <div className={styles.buttonContainer}>
          <button
            type="button"
            className={`${styles.appButton} ${styles.unarchiveAppButton}`}
            aria-label="Unarchive App"
            disabled={categoriesIsLoading || archivedApplications.length === 0}
            onClick={() => setShowUnarchiveAppModal(true)}
          >
            <UnarchiveButton aria-hidden="true" />
          </button>
          <button
            className={styles.appButton}
            onClick={() => setShowArchiveAppModal(true)}
          >
            <ArchiveButton />
          </button>
          <button
            className={styles.appButton}
            onClick={() => setShowNewAppModal(true)}
          >
            <NewButton />
          </button>
        </div>
      </header>

      <div className={styles.content}>
        <Route path={`${path}/simplesellingframework`}>
          <SSF />
        </Route>

        <Switch>
          {optionList && optionList.length > 0 && (
            <>
              {optionList.map((option) => (
                <Route path={option.pathname}>
                  <AppCategories
                    appId={option.appId}
                    pageTitle={`${option.title}`}
                    showAppPreview={
                      option.pathname !== `${path}/simplesellingframework`
                    }
                  />
                </Route>
              ))}
            </>
          )}

          <Route path="*">
            <Redirect to={`${path}/retail`} />
          </Route>
        </Switch>
      </div>
    </Card>
  );
}
